/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable consistent-return */
import { Flex, Text } from '@leverege/ui-elements'
import moment from 'moment'
import { useMemo } from 'react'
import { formatPaceTimes, getBgColor, getStyle } from '../../Utils'
import S from '../ShellsTable.css'

const TRANSLATED_STAGES = [
  'Mold Prep',
  'Gelcoat',
  'Initial Lay-up',
  'Cmpnts & Core',
  'Final Lay-up',
  'Infusion Prep',
  'Infusion',
  'Resin Cure',
  'DeBg & Inspectn',
  'Assembly Prep',
]

const STAGES = [
  'Prep del Molde',
  'Gelcoat',
  'Coloc Inic',
  'Comp y Núc',
  'Coloc Fin',
  'Prep Infus',
  'Infusión',
  'Curado Resina',
  'Desemp & Insp',
  'Prep Ensam',
]

const useShellsTable = ( fgJob ) => {
  const getPercentage = stages => Object.values( stages )?.map( s => `${s?.completionPercentage ?? 0}%` )
  const getTarget = stages => Object.values( stages )?.map( s => formatPaceTimes( s?.targetDuration / 3600 ) )
  const getPaces = stages => Object.values( stages )?.map( s => formatPaceTimes( s?.actualDuration / 3600 ) )

  const getLastStage = stages => Object.values( stages ).findIndex( s => !!s?.startTime && !s?.endTime )

  const getInProgress = ( stages ) => {
    const lastStage = getLastStage( stages )

    return !( lastStage < 0 )
  }

  const getStartTimes = stages => Object.values( stages ).map( s => s.startTime )
  const getEndTimes = stages => Object.values( stages ).map( s => s.endTime )
  const getTimings = ( startTimes, endTimes ) => startTimes.map( ( st, i ) => [ st, endTimes[i] ] )

  const ssStages = fgJob?.suctionSideJob?.data?.stage ?? {}
  const psStages = fgJob?.pressureSideJob?.data?.stage ?? {}

  const data = useMemo(
    () => [
      ...STAGES.map( ( s, i ) => {
        return {
          ssStages,
          psStages,
          id : s + i,
          rowTitle : s,
          rowSub : TRANSLATED_STAGES[i],
          ssTarget : getTarget( ssStages )[i],
          psTarget : getTarget( psStages )[i],
          ss : getPaces( ssStages )[i],
          ps : getPaces( psStages )[i],
          ssInProgress : getInProgress( ssStages ),
          psInProgress : getInProgress( psStages ),
          ssInspection : getPercentage( ssStages )[i],
          psInspection : getPercentage( psStages )[i],
          ssTimings : getTimings( getStartTimes( ssStages ), getEndTimes( ssStages ) )[
            i
          ],
          psTimings : getTimings( getStartTimes( psStages ), getEndTimes( psStages ) )[
            i
          ],
        }
      } ),
    ],
    [ fgJob ],
  )

  const columns = [
    {
      id : 'Stage',
      Header : (
        <Text variant='columnHeaderCustom' style={{ fontSize : 13 }}>
          Stage
        </Text>
      ),
      accessor : 'rowTitle',
      Cell : ( { value, row } ) => {
        return (
          <Text variant='columnHeaderCustom'>
            <Flex align='center' direction='column'>
              <Text
                variant='columnLabelCustomVertical'
                style={{ fontSize : 11 }}>
                {value}
              </Text>
              <Text variant='columnSubCustomVertical' style={{ fontSize : 11 }}>
                {row?.original?.rowSub}
              </Text>
            </Flex>
          </Text>
        )
      },
      sorted : false,
    },
    {
      id : 'ssInspection',
      Header : (
        <Text variant='columnHeaderCustom' style={{ fontSize : 13 }}>
          Inspection
        </Text>
      ),
      accessor : 'ssInspection',
      Cell : ( { value, ...props } ) => {
        const data = props?.row?.original
        const timings = data?.ssTimings

        return (
          <Flex
            align='center'
            justify='center'
            className={S.row}
            style={{
              fontSize : 14,
              backgroundColor : getBgColor( {
                value,
                hasColor : true,
                rowTitle : 'Inspection',
                timings,
                ...props,
              } ),
            }}>
            {value}
          </Flex>
        )
      },
      sorted : false,
    },
    {
      id : 'SS Target',
      Header : (
        <Text variant='columnHeaderCustom' style={{ fontSize : 13 }}>
          SS Target
        </Text>
      ),
      accessor : 'ssTarget',
      Cell : ( { value } ) => (
        <Text variant='columnHeaderCustom' style={{ fontSize : 14 }}>
          {value}
        </Text>
      ),
      sorted : false,
    },
    {
      id : 'SS',
      Header : (
        <Flex variant='col' align='center'>
          <Text
            variant='columnHeaderCustom'
            style={{ fontSize : 13, lineHeight : 1.5 }}>
            SS
          </Text>
          <Text
            variant='columnHeaderSubCustom'
            style={{ fontSize : 13, lineHeight : 1.5 }}>
            Actual CT
          </Text>
        </Flex>
      ),
      accessor : 'ss',
      Cell : ( { value, ...props } ) => {
        const i = props?.row?.index
        const stages = props?.row?.original?.ssStages ?? {}
        const isLast = i === getLastStage( stages )
        const isFinished = !props?.row?.original?.ssInProgress
        const targetPace = props?.row?.original?.ssTarget

        const partialDuration = () => {
          const lastStageIndex = getLastStage( stages )
          const startTime = Object.values( stages )[lastStageIndex]?.startTime

          return formatPaceTimes( moment().diff( startTime, 's' ) / 3600 ) ?? '0.00'
        }

        return (
          <Flex
            align='center'
            justify='center'
            className={S.row}
            style={{
              backgroundColor : getBgColor( {
                value : isLast ? partialDuration() : value,
                i,
                isLast,
                targetPace,
                isFinished,
                hasColor : true,
                ...props,
              } ),
              ...getStyle( {
                value : isLast ? partialDuration() : value,
                i,
                isLast,
                targetPace,
                isFinished,
                hasColor : true,
                ...props,
              } ),
            }}>
            {isLast ? partialDuration() : value ?? '-'}
          </Flex>
        )
      },
      sorted : false,
    },
    {
      id : 'PS',
      Header : (
        <Flex variant='col' align='center'>
          <Text
            variant='columnHeaderCustom'
            style={{ fontSize : 13, lineHeight : 1.5 }}>
            PS
          </Text>
          <Text
            variant='columnHeaderSubCustom'
            style={{ fontSize : 13, lineHeight : 1.5 }}>
            Actual CT
          </Text>
        </Flex>
      ),
      accessor : 'ps',
      Cell : ( { value, ...props } ) => {
        const i = props?.row?.index
        const stages = props?.row?.original?.psStages ?? {}
        const isLast = i === getLastStage( stages )
        const isFinished = !props?.row?.original?.psInProgress
        const targetPace = props?.row?.original?.psTarget

        const partialDuration = () => {
          const lastStageIndex = getLastStage( stages )
          const startTime = Object.values( stages )[lastStageIndex]?.startTime

          return formatPaceTimes( moment().diff( startTime, 's' ) / 3600 ) ?? '0.00'
        }

        return (
          <Flex
            align='center'
            justify='center'
            className={S.row}
            style={{
              backgroundColor : getBgColor( {
                value : isLast ? partialDuration() : value,
                i,
                isLast,
                targetPace,
                isFinished,
                hasColor : true,
                ...props,
              } ),
              ...getStyle( {
                value : isLast ? partialDuration() : value,
                i,
                isLast,
                targetPace,
                isFinished,
                hasColor : true,
                ...props,
              } ),
            }}>
            {isLast ? partialDuration() : value ?? '-'}
          </Flex>
        )
      },
      sorted : false,
    },
    {
      id : 'PS Target',
      Header : (
        <Text variant='columnHeaderCustom' style={{ fontSize : 13 }}>
          PS Target
        </Text>
      ),
      accessor : 'psTarget',
      Cell : ( { value } ) => (
        <Text variant='columnHeaderCustom' style={{ fontSize : 14 }}>
          {value}
        </Text>
      ),
      sorted : false,
    },
    {
      id : 'psInspection',
      Header : (
        <Text variant='columnHeaderCustom' style={{ fontSize : 13 }}>
          Inspection
        </Text>
      ),
      accessor : 'psInspection',
      Cell : ( { value, ...props } ) => {
        const data = props?.row?.original
        const timings = data?.psTimings

        return (
          <Flex
            align='center'
            justify='center'
            className={S.row}
            style={{
              fontSize : 14,
              backgroundColor : getBgColor( {
                value,
                hasColor : true,
                rowTitle : 'Inspection',
                timings,
                ...props,
              } ),
            }}>
            {value}
          </Flex>
        )
      },
      sorted : false,
    },
  ]

  return {
    data,
    columns,
  }
}

export default useShellsTable
